import React from "react";

import { css } from "@emotion/css";
import { captureException } from "@sentry/react";
import { QueryAwait } from "@steto-care/steto-query";

import { useQueryDateRange } from "@/hooks/query/useQueryDateRange.ts";
import { usePatientFromURL } from "@/hooks/usePatientIdFromURL.ts";
import { useStyles } from "@/hooks/useStyles.ts";
import { BGMTableCard } from "@/pages/patient-monitoring/BGMTableCard.tsx";
import { HeaderMonitoring } from "@/pages/patient-monitoring/HeaderMonitoring.tsx";
import { getDateRangeAndPeriodicity, periodicity14Days } from "@/pages/patient-monitoring/PatientMonitoringPeriod.ts";
import {
  PatientMonitoringProvider,
  useListenToQueryDateRange,
  usePatientMonitoringContext,
} from "@/pages/patient-monitoring/PatientMonitoringState.tsx";
import { Queries } from "@/queries/Queries.ts";
import { Theme } from "@/theme/CreateTheme.ts";
import { CenteredLoader } from "@/uiKit/atoms/Loader.tsx";
import DailyFileReportEmbed from "@/uiKit/organisms/DailyFileReportEmbed.tsx";
import { DailyGraphList } from "@/uiKit/organisms/DailyGraphList/DailyGraphList.tsx";
import { DiabetesStatsBGM } from "@/uiKit/organisms/DiabetesStats/DiabetesStatsBGM.tsx";
import { DiabetesStatsCGM } from "@/uiKit/organisms/DiabetesStats/DiabetesStatsCGM.tsx";
import { HandledErrorCard } from "@/uiKit/organisms/HandledErrorCard.tsx";
import { PatientDetailNudges } from "@/uiKit/organisms/PatientDetailNudges/PatientDetailNudges.tsx";

const ASIDE_BASIS_PX = 300;

export const PatientMonitoring: React.FC = () => {
  const patient = usePatientFromURL();
  const me = Queries.user.usePractitionerMe({});
  const queryData = useQueryDateRange();

  const periodAndRange = getDateRangeAndPeriodicity(queryData);
  const styles = useStyles(makeStyles);

  return (
    <PatientMonitoringProvider
      periodicity={periodAndRange.periodicity}
      from={periodAndRange.from}
      to={periodAndRange.to}
      practitioner={me}
      patient={patient}
    >
      <QueryAwait errorFallback={HandledErrorCard} pendingFallback={CenteredLoader}>
        <PatientDetailNudges />
        <div className={styles.container}>
          <div className={styles.content}>
            <PatientMonitoringContent />
          </div>
        </div>
      </QueryAwait>
    </PatientMonitoringProvider>
  );
};

const PatientMonitoringContent: React.FC = () => {
  useListenToQueryDateRange();
  const state = usePatientMonitoringContext(state => state);
  const displayMode = Queries.practitioner.useDiabetesDisplayMode(state.patient.id);
  const isLogbook = displayMode.glycemiaDataDisplayMode === "logbook";
  const isCGM = displayMode.glycemiaDataDisplayMode === "continuous";
  const isInsulinPump =
    displayMode.insulinIndicatorsDisplayMode === "continuous" ||
    displayMode.insulinIndicatorsDisplayMode === "closed_loop";
  const stats_display = displayMode.glycemiaIndicatorsDisplayMode;

  switch (true) {
    case displayMode.isMedtronicClosedLoop: {
      return (
        <>
          <HeaderMonitoring lockPeriod={{ periodicity: periodicity14Days }} />
          {stats_display === "cgm" && (
            <DiabetesStatsCGM closedLoop={true} patientId={state.patient.id} from={state.from} to={state.to} />
          )}
          <DailyFileReportEmbed patient={state.patient} date={state.to} />
        </>
      );
    }
    case isLogbook && isInsulinPump:
      captureException(new Error("Cannot display BGM Logbook with pump data."));
      return <CenteredLoader />;

    case isLogbook:
      return (
        <>
          <HeaderMonitoring />
          {stats_display === "bgm" ? (
            <DiabetesStatsBGM patientId={state.patient.id} from={state.from} to={state.to} />
          ) : (
            <DiabetesStatsCGM
              patientId={state.patient.id}
              from={state.from}
              to={state.to}
              insulinInjectionMethod={isInsulinPump ? "pump" : "pen"}
              closedLoop={displayMode.insulinIndicatorsDisplayMode === "closed_loop"}
            />
          )}
          <BGMTableCard patient={state.patient} practitioner={state.practitioner} date={state.to} />
        </>
      );
    case isCGM:
    default:
      return (
        <>
          <HeaderMonitoring />
          {stats_display === "bgm" ? (
            <DiabetesStatsBGM patientId={state.patient.id} from={state.from} to={state.to} />
          ) : (
            <DiabetesStatsCGM
              patientId={state.patient.id}
              from={state.from}
              to={state.to}
              insulinInjectionMethod={isInsulinPump ? "pump" : "pen"}
              closedLoop={displayMode.insulinIndicatorsDisplayMode === "closed_loop"}
            />
          )}
          <DailyGraphList
            {...state}
            isDexcom={displayMode.glycemiaDeviceName === "dexcom"}
            insulinDisplayMode={displayMode.insulinDataDisplayMode ?? "discrete"}
          />
        </>
      );
  }
};

const makeStyles = (theme: Theme) => ({
  container: css`
    display: flex;
    flex-direction: row;
    width: 100%;
    justify-content: center;
    gap: ${theme.spacing(8)};
    margin-bottom: ${theme.spacing(12)};
  `,
  content: css`
    display: flex;
    flex-direction: column;
    flex: 1 1;
    gap: ${theme.spacing(8)};
    min-width: 0;
  `,
  aside: css`
    display: flex;
    flex-direction: column;
    flex: 0 1 ${ASIDE_BASIS_PX}px;
    gap: ${theme.spacing(8)};
    padding-top: ${theme.spacing(28)};
  `,
  inlinedAside: css`
    width: 100%;
    display: flex;
    flex-direction: row;
    gap: ${theme.spacing(8)};
    flex-wrap: wrap;
  `,
  asideContent: css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing(8)};
    flex: 1 1 0;
    height: fit-content;
  `,
  loading: css`
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
  `,
});
